/*
 * Use this composable when you want to load the Google Maps JavaScript API on a page
 * Includes progressive backoff retry logic and detailed error logging
 * Also provides address autocomplete functionality
 *
 * Shared core: this is the single Google Maps loader + address lookup for every
 * consuming app. It imports its Vue/Nuxt symbols explicitly (vue + #imports)
 * rather than leaning on the host app's auto-import, so it resolves the same way
 * in every consumer. onMounted comes from #imports (Nuxt re-exports the Vue
 * lifecycle hooks there) so test harnesses that mock the Nuxt auto-import layer
 * still intercept it.
 */

import { ref, readonly, watch, toValue } from "vue";
import type { MaybeRefOrGetter } from "vue";
import { useRuntimeConfig, useState, onMounted } from "#imports";

import type { GoogleMapsPlaceDetailsFormatted } from "../types/google";
import type { GooglePlaceAddress, GeocodeResult, WaypointLocality, WaypointPlaceType } from "../types";
import { componentsToWaypointSuburb, parseGooglePlacesAddressComponents } from "../utils/google";

interface RetryOptions {
	maxAttempts?: number;
	initialDelay?: number;
	maxDelay?: number;
	backoffMultiplier?: number;
}

interface GoogleMapsOptions {
	retryOptions?: RetryOptions;
	locality?: MaybeRefOrGetter<WaypointLocality | undefined>;
	localities?: MaybeRefOrGetter<WaypointLocality[] | undefined>;
	// When false, do NOT load the Maps script on mount. Use this for a consumer
	// that only calls a lookup helper (e.g. reverseGeocode) after some OTHER
	// consumer has already loaded the SDK, so it doesn't pull Google's script into
	// a page that would otherwise defer it (e.g. a landing/service step before the
	// address field mounts). Defaults to true (load eagerly on mount, as before).
	autoload?: boolean;
}

// Shared loading promise to prevent multiple simultaneous load attempts
let loadingPromise: Promise<void> | null = null;

export const useGoogleMaps = (options?: GoogleMapsOptions) => {
	const config = useRuntimeConfig();
	const isLoading = ref(false);
	const isLoaded = useState<boolean>("google-maps-loaded", () => false);
	const error = ref<Error | null>(null);

	const { maxAttempts = 5, initialDelay = 1000, maxDelay = 30000, backoffMultiplier = 2 } = options?.retryOptions || {};

	const locality = options?.locality;
	const localities = options?.localities;

	const sleep = (ms: number): Promise<void> => {
		return new Promise((resolve) => setTimeout(resolve, ms));
	};

	const loadGoogleMapsScript = async (): Promise<void> => {
		// Check both the window object and our state
		if (window.google?.maps || isLoaded.value) {
			console.log("[Google Maps] Already loaded");
			return Promise.resolve();
		}

		// Check if another loader (e.g. @googlemaps/js-api-loader) has already
		// injected a Google Maps script tag. If so, wait for it instead of
		// adding a duplicate, then ensure the places library is available.
		const existingScript = document.querySelector('script[src*="maps.googleapis.com/maps/api/js"]');
		if (existingScript) {
			console.log("[Google Maps] Existing script tag detected, waiting for it to load");
			await new Promise<void>((resolve, reject) => {
				if (window.google?.maps) {
					resolve();
					return;
				}

				let timeoutId: ReturnType<typeof setTimeout> | undefined;
				const checkInterval = setInterval(() => {
					if (window.google?.maps) {
						clearInterval(checkInterval);
						if (timeoutId) clearTimeout(timeoutId);
						resolve();
					}
				}, 100);

				// Timeout after 15 seconds
				if (!import.meta.test) {
					timeoutId = setTimeout(() => {
						clearInterval(checkInterval);
						if (!window.google?.maps) {
							reject(new Error("Google Maps loading timeout - existing script never completed"));
						}
					}, 15000);
				}
			});

			// The existing script may not have loaded the places library,
			// so dynamically import it if needed
			if (!google.maps.places) {
				await google.maps.importLibrary("places");
			}
			return;
		}

		return new Promise<void>((resolve, reject) => {
			let timeoutId: ReturnType<typeof setTimeout> | null = null;

			try {
				const script = document.createElement("script");
				const callbackName = `initGoogleMaps_${Date.now()}`;

				(window as any)[callbackName] = () => {
					delete (window as any)[callbackName];
					if (timeoutId) clearTimeout(timeoutId);
					resolve();
				};

				script.src = `https://maps.googleapis.com/maps/api/js?key=${config.public.googlePlacesApiKey}&libraries=places&v=beta&callback=${callbackName}&loading=async`;
				script.async = true;
				script.defer = true;

				script.onerror = (event) => {
					delete (window as any)[callbackName];
					if (timeoutId) clearTimeout(timeoutId);
					//@ts-expect-error
					reject(new Error(`Failed to load Google Maps script: ${event.type}`));
				};

				document.head.appendChild(script);

				if (!import.meta.test) {
					timeoutId = setTimeout(() => {
						if ((window as any)[callbackName]) {
							delete (window as any)[callbackName];
							reject(new Error("Google Maps loading timeout - callback was never called"));
						}
					}, 15000);
				}
			} catch (err) {
				console.error("[Google Maps] Exception during script creation:", err);
				if (timeoutId) clearTimeout(timeoutId);
				reject(err);
			}
		});
	};

	const loadWithRetry = async (): Promise<void> => {
		let lastError: Error | null = null;

		for (let attempt = 1; attempt <= maxAttempts; attempt++) {
			try {
				await loadGoogleMapsScript();
				isLoaded.value = true;
				error.value = null;
				return;
			} catch (err) {
				lastError = err instanceof Error ? err : new Error(String(err));
				console.error(`[Google Maps] Attempt ${attempt} failed:`, lastError.message);

				if (attempt < maxAttempts) {
					const delay = Math.min(initialDelay * Math.pow(backoffMultiplier, attempt - 1), maxDelay);
					await sleep(delay);
				} else {
					console.error(`[Google Maps] All ${maxAttempts} attempts failed`);
				}
			}
		}

		error.value = lastError;
		throw lastError;
	};

	onMounted(async () => {
		if (import.meta.browser && options?.autoload !== false) {
			// Already loaded, nothing to do
			if (isLoaded.value || window.google?.maps) {
				isLoaded.value = true;
				return;
			}

			// If another call is already loading, wait for it
			if (loadingPromise) {
				try {
					await loadingPromise;
				} catch {
					// The original caller will handle the error
				}
				return;
			}

			isLoading.value = true;
			loadingPromise = loadWithRetry();

			try {
				await loadingPromise;
			} catch (err) {
				console.error("[Google Maps] Failed to load after all retries:", err);
			} finally {
				isLoading.value = false;
				loadingPromise = null;
			}
		}
	});

	const sessionToken = ref<google.maps.places.AutocompleteSessionToken | null>(null);
	const localityBounds = ref<google.maps.LatLngBounds | undefined>(undefined);

	function createBoundsFromLocality(loc: WaypointLocality): google.maps.LatLngBounds | undefined {
		const center = new google.maps.LatLng(loc.lat, loc.lng);
		const radiusInMeters = loc.radiusKms * 1000;

		const circle = new google.maps.Circle({
			center: center,
			radius: radiusInMeters,
		});

		return circle.getBounds() ?? undefined;
	}

	function createCircleBounds(): google.maps.LatLngBounds | undefined {
		// Google Maps API must be loaded before we can create bounds
		if (!window.google?.maps) {
			return undefined;
		}

		// Multiple localities: compute union of all circle bounds
		const locs = toValue(localities);
		if (locs && locs.length > 0) {
			let unionBounds: google.maps.LatLngBounds | undefined;

			for (const loc of locs) {
				const bounds = createBoundsFromLocality(loc);
				if (bounds) {
					if (!unionBounds) {
						unionBounds = bounds;
					} else {
						unionBounds.union(bounds);
					}
				}
			}

			return unionBounds;
		}

		// Single locality: original behaviour
		const loc = toValue(locality);
		if (loc) {
			return createBoundsFromLocality(loc);
		}

		console.warn("[Google Maps] No locality or localities defined for bounds creation");
		return undefined;
	}

	if (toValue(locality)) {
		watch(
			() => toValue(locality),
			(newLocality, oldLocality) => {
				if (newLocality !== oldLocality?.localityId) {
					localityBounds.value = createCircleBounds();
				}
			},
			{ immediate: true, deep: true }
		);
	}

	if (toValue(localities)) {
		watch(
			() => toValue(localities),
			() => {
				localityBounds.value = createCircleBounds();
			},
			{ immediate: true, deep: true }
		);
	}

	async function autocompleteAddress(inputPartialString: string, primaryType: WaypointPlaceType | null, suggestionCount: number = 5): Promise<GoogleMapsPlaceDetailsFormatted[]> {
		if (inputPartialString.length <= 1) {
			return [];
		}

		if (!sessionToken.value) {
			sessionToken.value = new google.maps.places.AutocompleteSessionToken();
		}

		try {
			let primaryTypeValue: string[] | undefined = undefined;
			if (primaryType === "hotel") {
				primaryTypeValue = ["hotel", "motel", "resort_hotel", "lodging", "bed_and_breakfast"];
			} else if (primaryType === "airport") {
				primaryTypeValue = ["airport", "international_airport"];
			}

			const request = {
				input: inputPartialString,
				sessionToken: sessionToken.value,
				locationRestriction: createCircleBounds(),
				includedPrimaryTypes: primaryTypeValue,
			};

			const { suggestions } = await google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions(request);

			const filteredSuggestions = suggestions.filter((suggestion: google.maps.places.AutocompleteSuggestion) => {
				return suggestion !== undefined;
			});

			const formattedSuggestions: GoogleMapsPlaceDetailsFormatted[] = [];

			for (let i = 0; i < suggestionCount && i < filteredSuggestions.length; i++) {
				const suggestion = filteredSuggestions[i];
				if (suggestion === undefined) {
					continue;
				}

				const prediction = suggestion.placePrediction?.text.text;

				if (!prediction) {
					console.warn("[Google Maps] Prediction is undefined");
					continue;
				}

				formattedSuggestions.push({
					formattedAddress: prediction,
					autocompleteSuggestion: suggestion,
				});
			}

			return formattedSuggestions;
		} catch (err) {
			console.error("[Google Maps] Error fetching autocomplete suggestions:", err);
			return [];
		}
	}

	async function selectAutocompleteSuggestion(suggestion: google.maps.places.AutocompleteSuggestion): Promise<{
		waypoint: GooglePlaceAddress;
		isAirport: boolean;
		isHotel: boolean;
		parsedComponents: Record<string, string>;
		// The place's Google types (e.g. ["street_address"], ["locality","political"],
		// ["administrative_area_level_1","political"]). Lets callers reject a
		// too-broad selection (a whole state/region/postcode); see placeGranularity.
		types: string[];
	}> {
		if (suggestion === undefined) {
			throw new Error("Suggestion is undefined");
		}

		const placePrediction = suggestion.placePrediction;
		if (placePrediction === null) {
			throw new Error("Place prediction is null");
		}

		const autocompleteSuggestionToPlace = placePrediction.toPlace();

		// *WARNING* The user is about to be billed for this request
		await autocompleteSuggestionToPlace.fetchFields({
			fields: ["displayName", "formattedAddress", "addressComponents", "location", "types"],
		});

		const airportType = autocompleteSuggestionToPlace.types && (autocompleteSuggestionToPlace.types.includes("airport") || autocompleteSuggestionToPlace.types.includes("international_airport"));

		const hotelType = autocompleteSuggestionToPlace.types && (autocompleteSuggestionToPlace.types.includes("hotel") || autocompleteSuggestionToPlace.types.includes("lodging"));

		// The sessionToken has now ended.
		sessionToken.value = null;

		try {
			const validatedAddressComponents = autocompleteSuggestionToPlace.addressComponents;

			const displayName = autocompleteSuggestionToPlace.displayName;
			const formattedGooglePlaceAddress = autocompleteSuggestionToPlace.formattedAddress;
			const relevantAddressComponents = parseGooglePlacesAddressComponents(validatedAddressComponents as any);
			const addressLat = autocompleteSuggestionToPlace.location?.lat();
			const addressLng = autocompleteSuggestionToPlace.location?.lng();

			if (!addressLat || !addressLng) {
				throw new Error("Address latitude or longitude is undefined");
			}

			if (!formattedGooglePlaceAddress) {
				throw new Error("Formatted Google Place Address is undefined");
			}

			const newWaypoint: GooglePlaceAddress = {
				address: "",
				address1: "",
				name: "",
				suburb: "",
				state: "",
				postCode: "",
				region: "",
				lat: "",
				lng: "",
			};

			// Avoid adding the display name twice to the address field
			if (displayName && formattedGooglePlaceAddress.includes(displayName)) {
				newWaypoint.address = formattedGooglePlaceAddress;
			} else {
				newWaypoint.address = `${displayName}, ${formattedGooglePlaceAddress}`;
			}

			if (displayName && relevantAddressComponents.streetNumber && displayName.includes(relevantAddressComponents.streetNumber)) {
				newWaypoint.name = "";
			} else if (displayName) {
				newWaypoint.name = displayName;
			} else {
				newWaypoint.name = relevantAddressComponents.route || "";
			}

			// If we have an addressComponents.streetNumber, append the street number. If not then just use the route
			if (relevantAddressComponents.streetNumber !== undefined) {
				newWaypoint.address1 = `${relevantAddressComponents.streetNumber} ${relevantAddressComponents.route}`;
			} else {
				newWaypoint.address1 = relevantAddressComponents.route ?? "";
			}

			if (newWaypoint.address1 === "" || newWaypoint.address1 === undefined || newWaypoint.address1 === null) {
				newWaypoint.address1 = newWaypoint.name;
				newWaypoint.name = "";
			}

			newWaypoint.suburb = componentsToWaypointSuburb(relevantAddressComponents);
			newWaypoint.state = relevantAddressComponents.state ?? "";
			newWaypoint.postCode = relevantAddressComponents.postalCode ?? "";
			newWaypoint.lat = addressLat.toString();
			newWaypoint.lng = addressLng.toString();
			newWaypoint.region = relevantAddressComponents.region ?? "";

			return {
				waypoint: newWaypoint,
				isAirport: airportType || false,
				isHotel: hotelType || false,
				parsedComponents: relevantAddressComponents,
				types: autocompleteSuggestionToPlace.types ?? [],
			};
		} catch (err) {
			console.error("[Google Maps] Error selecting autocomplete suggestion:", err);
			throw err;
		}
	}

	/**
	 * Resets the autocomplete session token.
	 * Call this when starting a new autocomplete session or clearing results.
	 */
	function resetAutocompleteSession(): void {
		sessionToken.value = null;
	}

	/**
	 * Geocode an address string to get lat/lng coordinates and structured address components.
	 * Uses Google Maps Geocoder API.
	 * @param address - The address string to geocode
	 * @returns Promise with lat/lng, formattedAddress, and addressComponents or null if not found
	 */
	async function geocodeAddress(address: string): Promise<GeocodeResult | null> {
		if (!address || address.trim().length === 0) {
			return null;
		}

		try {
			const geocoder = new google.maps.Geocoder();

			const response = await geocoder.geocode({ address });

			const firstResult = response.results?.[0];
			if (firstResult) {
				const location = firstResult.geometry.location;
				return {
					lat: location.lat(),
					lng: location.lng(),
					formattedAddress: firstResult.formatted_address ?? "",
					addressComponents: firstResult.address_components ?? [],
					types: firstResult.types ?? [],
				};
			}

			console.warn("[Google Maps] No geocode results found for address:", address);
			return null;
		} catch (err) {
			console.error("[Google Maps] Error geocoding address:", err);
			return null;
		}
	}

	/**
	 * Geocode multiple addresses in parallel.
	 * @param addresses - Array of address strings to geocode
	 * @returns Promise with array of results or null for each address
	 */
	async function geocodeAddresses(addresses: string[]): Promise<(GeocodeResult | null)[]> {
		return Promise.all(addresses.map((address) => geocodeAddress(address)));
	}

	/**
	 * Reverse-geocode a coordinate to structured address components.
	 * Mirror of geocodeAddress but keyed on lat/lng, used to recover fields the
	 * forward Places/geocoder lookup omitted (notably `postal_code`, which Google
	 * frequently drops for CBD routes/POIs and airports but reliably returns for a
	 * precise coordinate). *WARNING* the caller is billed for this request.
	 * @returns the nearest addressed result, or null if none / on error.
	 */
	async function reverseGeocode(lat: number, lng: number): Promise<GeocodeResult | null> {
		if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
			return null;
		}

		try {
			const geocoder = new google.maps.Geocoder();

			const response = await geocoder.geocode({ location: { lat, lng } });

			const firstResult = response.results?.[0];
			if (firstResult) {
				const location = firstResult.geometry.location;
				return {
					lat: location.lat(),
					lng: location.lng(),
					formattedAddress: firstResult.formatted_address ?? "",
					addressComponents: firstResult.address_components ?? [],
					types: firstResult.types ?? [],
				};
			}

			console.warn("[Google Maps] No reverse-geocode results found for coordinate:", lat, lng);
			return null;
		} catch (err) {
			console.error("[Google Maps] Error reverse-geocoding coordinate:", err);
			return null;
		}
	}

	return {
		isLoading: readonly(isLoading),
		isLoaded: readonly(isLoaded),
		error: readonly(error),
		retry: loadWithRetry,

		// Autocomplete functionality
		autocompleteAddress,
		selectAutocompleteSuggestion,
		resetAutocompleteSession,

		// Geocoding
		geocodeAddress,
		geocodeAddresses,
		reverseGeocode,
	};
};
